import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Package, MapPin, Phone, Mail, Truck, CreditCard, Calendar, DollarSign, FileText, Download, Printer, X, CheckCircle, XCircle, Clock, AlertCircle, MessageSquare, WrenchIcon, ExternalLink, ChevronDown, CheckSquare, Copy, RefreshCw } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
// Invoice PDF is now generated internally via /api/invoices/{id}/pdf
import DelhiveryShipping from '../components/shipping/DelhiveryShipping';
import ShipNowModal from '../components/shipping/ShipNowModal';
import DeliveryLocationCard from '../components/shipping/DeliveryLocationCard';
import PickupScheduler from '../components/PickupScheduler';
import OrderNotesCard from '../components/OrderNotesCard';
// iter-230l — Admin partial-refund modal & history card (super_admin only)
import RefundModal from '../components/admin/RefundModal';
import RefundHistoryCard from '../components/admin/RefundHistoryCard';
import { downloadAuthedFile } from '../utils/downloadAuthedFile';
import { useShipmentState } from '../hooks/useShipmentState';

// iter-412 — maps a listing's `listing_type` to its view-page route
// segment, so each Order Item's "View Product" link opens the correct
// category page (falls back to 'products' for unknown/legacy types).
const LISTING_TYPE_TO_ROUTE = {
  product: 'products',
  service: 'services',
  rental: 'rentals',
  subscription: 'subscriptions',
  event: 'events',
  classified: 'classifieds',
  volunteering: 'volunteering',
};


// ── Sprint-D: Refund Status Card ─────────────────────────────────────────────

const REFUND_STATUS_META = {
  not_required:  { label: 'Not Required',          color: 'text-gray-500',   bg: 'bg-gray-50',    border: 'border-gray-200',   Icon: CheckCircle },
  eligible:      { label: 'Eligible',               color: 'text-blue-700',   bg: 'bg-blue-50',    border: 'border-blue-200',   Icon: CheckCircle },
  pending:       { label: 'Pending',                color: 'text-orange-700', bg: 'bg-orange-50',  border: 'border-orange-200', Icon: Clock },
  no_payment_id: { label: 'Pending — No Payment ID',color: 'text-red-700',    bg: 'bg-red-50',     border: 'border-red-200',    Icon: AlertCircle },
  processing:    { label: 'Processing',             color: 'text-blue-700',   bg: 'bg-blue-50',    border: 'border-blue-200',   Icon: Clock },
  completed:     { label: 'Completed',              color: 'text-green-700',  bg: 'bg-green-50',   border: 'border-green-200',  Icon: CheckCircle },
  failed:        { label: 'Failed',                 color: 'text-red-700',    bg: 'bg-red-50',     border: 'border-red-200',    Icon: XCircle },
  offline:       { label: 'Recorded Offline',       color: 'text-purple-700', bg: 'bg-purple-50',  border: 'border-purple-200', Icon: CheckCircle },
};

function RefundStatusCard({ order, onRefundClick, onRepairClick }) {
  const meta = REFUND_STATUS_META[order.refund_status] || REFUND_STATUS_META.pending;
  const { Icon } = meta;
  return (
    <Card className={`border ${meta.border} ${meta.bg}`} data-testid="refund-status-card">
      <CardContent className="pt-4 pb-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <Icon className={`h-5 w-5 ${meta.color} flex-shrink-0`} />
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Refund Status</p>
              <p className={`text-base font-semibold ${meta.color}`} data-testid="refund-status-label">{meta.label}</p>
              {order.refund_amount && (
                <p className="text-sm text-gray-600 mt-0.5">₹{Number(order.refund_amount).toLocaleString('en-IN')}</p>
              )}
              {order.refund_payment_id && (
                <p className="text-xs text-gray-400 mt-0.5 font-mono">{order.refund_payment_id}</p>
              )}
              {order.refund_status === 'no_payment_id' && (
                <p className="text-xs text-red-600 mt-1">No Razorpay payment ID stored. Auto-repair can fetch it from Shopify.</p>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-2 flex-shrink-0">
            {order.refund_status === 'no_payment_id' && (
              <Button size="sm" variant="outline" className="border-orange-300 text-orange-700 text-xs"
                onClick={onRepairClick} data-testid="repair-payment-id-btn">
                <WrenchIcon className="h-3 w-3 mr-1" /> Repair Payment ID
              </Button>
            )}
            {['pending', 'no_payment_id', 'failed'].includes(order.refund_status) && onRefundClick && (
              <Button size="sm" variant="outline" className="border-red-300 text-red-700 text-xs"
                onClick={onRefundClick} data-testid="process-refund-status-btn">
                <DollarSign className="h-3 w-3 mr-1" /> Process Refund
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Sprint-D: Dynamic Event Timeline ─────────────────────────────────────────

const EVENT_META = {
  ORDER_PLACED:            { color: 'bg-green-500',  ring: 'ring-green-200',  text: 'text-green-700'  },
  PAYMENT_CONFIRMED:       { color: 'bg-green-500',  ring: 'ring-green-200',  text: 'text-green-700'  },
  PROCESSING:              { color: 'bg-blue-400',   ring: 'ring-blue-200',   text: 'text-blue-700'   },
  SHIPPED:                 { color: 'bg-blue-500',   ring: 'ring-blue-200',   text: 'text-blue-700'   },
  DELIVERED:               { color: 'bg-green-500',  ring: 'ring-green-200',  text: 'text-green-700'  },
  ORDER_CANCELLED:         { color: 'bg-red-500',    ring: 'ring-red-200',    text: 'text-red-700'    },
  REFUND_INITIATED:        { color: 'bg-amber-400',  ring: 'ring-amber-200',  text: 'text-amber-700'  },
  REFUND_COMPLETED:        { color: 'bg-green-500',  ring: 'ring-green-200',  text: 'text-green-700'  },
  REFUND_FAILED:           { color: 'bg-red-500',    ring: 'ring-red-200',    text: 'text-red-700'    },
  REFUND_RECORDED_OFFLINE: { color: 'bg-purple-500', ring: 'ring-purple-200', text: 'text-purple-700' },
  REFUND_PENDING:          { color: 'bg-amber-300',  ring: 'ring-amber-100',  text: 'text-amber-700'  },
  REFUND_NOT_REQUIRED:     { color: 'bg-gray-400',   ring: 'ring-gray-200',   text: 'text-gray-600'   },
  COMMENT:                 { color: 'bg-gray-300',   ring: 'ring-gray-200',   text: 'text-gray-700'   },
  // Post-delivery payout lifecycle
  RETURN_WINDOW_OPEN:      { color: 'bg-amber-400',  ring: 'ring-amber-200',  text: 'text-amber-700'  },
  RETURN_WINDOW_CLOSED:    { color: 'bg-teal-500',   ring: 'ring-teal-200',   text: 'text-teal-700'   },
  VENDOR_PAYOUT_PROCESSING:{ color: 'bg-blue-400',   ring: 'ring-blue-200',   text: 'text-blue-700'   },
  VENDOR_PAYOUT_SETTLED:   { color: 'bg-emerald-600',ring: 'ring-emerald-200',text: 'text-emerald-700'},
};

function fmtDate(ts) {
  if (!ts) return '';
  try {
    return new Date(ts).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return ts; }
}

function EventTimeline({ events }) {
  if (!events || !events.length) {
    return <p className="text-sm text-gray-400 italic">No timeline events yet.</p>;
  }
  return (
    <div className="relative space-y-0" data-testid="event-timeline">
      {events.map((ev, i) => {
        const meta = EVENT_META[ev.type] || EVENT_META.PROCESSING;
        const isLast = i === events.length - 1;
        const isComment = ev.type === 'COMMENT';
        return (
          <div key={i} className="flex gap-4">
            <div className="flex flex-col items-center">
              <div className={`mt-1 h-3 w-3 rounded-full ring-4 ${meta.ring} ${ev.completed ? meta.color : 'bg-gray-200'} flex-shrink-0`} />
              {!isLast && <div className="flex-1 w-px bg-gray-200 my-1 min-h-[20px]" />}
            </div>
            <div className="pb-5 flex-1">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className={`text-sm font-medium ${ev.completed ? 'text-gray-900' : 'text-gray-400'}`}>
                  {isComment ? ev.detail : ev.label}
                </span>
                {ev.timestamp && <span className="text-xs text-gray-400">{fmtDate(ev.timestamp)}</span>}
                {isComment && <span className="text-xs text-gray-400 italic">— {ev.label}</span>}
              </div>
              {!isComment && ev.detail && <p className={`text-xs mt-0.5 ${meta.text}`}>{ev.detail}</p>}
              {ev.ref && <p className="text-xs font-mono text-gray-400 mt-0.5">{ev.ref}</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Sprint-D P2: Timeline Comment Input ──────────────────────────────────────

function TimelineCommentInput({ orderId, onAdded, apiCall }) {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await apiCall(`/api/orders/${orderId}/timeline-comment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: text.trim() }),
      });
      setText('');
      if (onAdded) onAdded();
    } catch (e) {
      toast({ title: 'Could not add note', description: e?.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-6 border-t border-gray-100 pt-4" data-testid="timeline-comment-input">
      <p className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
        <MessageSquare className="h-3 w-3" /> Add a note
      </p>
      <div className="flex gap-2">
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder="Leave a note visible to the team…"
          rows={2}
          className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-200"
        />
        <Button size="sm" onClick={handleSubmit} disabled={saving || !text.trim()} data-testid="add-timeline-comment-btn">
          {saving ? '…' : 'Post'}
        </Button>
      </div>
    </div>
  );
}

const OrderDetailsNew = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { apiCall, user } = useAuth();
  // iter-230l — Refund controls are super_admin only. Vendors must
  // not be able to even see the button; backend re-enforces with
  // `get_current_super_admin`. We accept both `super_admin` and
  // `admin` strings for forward-compat with role renames.
  const isSuperAdmin = ['super_admin', 'admin'].includes(user?.role);
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [refundRefreshKey, setRefundRefreshKey] = useState(0);
  // iter-298L — Item image lightbox
  const [lightboxImage, setLightboxImage] = useState(null);
  
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelDialog, setCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [rectifyDialog, setRectifyDialog] = useState(false);
  const [rectifyNote, setRectifyNote] = useState('');
  const [rectifying, setRectifying] = useState(false);
  const [rectifyResult, setRectifyResult] = useState(null);
  const [transferDialog, setTransferDialog] = useState(false);
  const [forceImmediate, setForceImmediate] = useState(false);
  const [transferNote, setTransferNote] = useState('');
  const [triggering, setTriggering] = useState(false);
  const [transferResult, setTransferResult] = useState(null);
  const [trackingInfo, setTrackingInfo] = useState(null);
  const [fetchingPaymentId, setFetchingPaymentId] = useState(false);
  const [showManualPaymentInput, setShowManualPaymentInput] = useState(false);
  const [manualPaymentId, setManualPaymentId] = useState('');
  const [savingManualPaymentId, setSavingManualPaymentId] = useState(false);
  const [delhiveryModalOpen, setDelhiveryModalOpen] = useState(false);
  const [shipNowOpen, setShipNowOpen] = useState(false);
  // Mark as Delivered override
  const [markDeliveredDialog, setMarkDeliveredDialog] = useState(false);
  const [markDeliveredNote, setMarkDeliveredNote] = useState('');
  const [markDeliveredTriggerPayout, setMarkDeliveredTriggerPayout] = useState(true);
  const [markingDelivered, setMarkingDelivered] = useState(false);
  const [markDeliveredResult, setMarkDeliveredResult] = useState(null);
  // Hyperlocal accept flow
  const [hyperlocalAccepting, setHyperlocalAccepting] = useState(false);
  const [hyperlocalReadying, setHyperlocalReadying] = useState(false);

  // iter-235 — SSOT shipment state. Combines order doc + tracking
  // info into a single source of truth. ALL CTA gating (Ship,
  // Print Label, Cancel AWB, Schedule Pickup, Track) reads from
  // `shipState.can('action_key')` — no surface re-derives state.
  // We merge tracking into the order so `useShipmentState` can use
  // the live shipment fields too.
  const shipmentForResolver = useMemo(() => {
    if (!order) return null;
    const sh = trackingInfo?.shipment;
    return { ...order, shipment: sh || order.shipment };
  }, [order, trackingInfo]);
  const shipState = useShipmentState(shipmentForResolver);

  // Retained for the audit-only banner / "Previous AWB" section in the
  // shipment card below. The action buttons themselves are gated by
  // `shipState.can('...')` exclusively.
  const awbIsActive = shipState.state === 'ACTIVE_AWB' ||
                      shipState.state === 'PICKUP_SCHEDULED' ||
                      shipState.state === 'IN_TRANSIT' ||
                      shipState.state === 'DELIVERED';
  const awbWasCancelled = shipState.state === 'AWB_CANCELLED';
  const [phoneInput, setPhoneInput] = useState('');
  const [savingPhone, setSavingPhone] = useState(false);
  const [awbCancelling, setAwbCancelling] = useState(false);
  // iter-229d — Customer-visible OTP for self-managed shipments.
  const [deliveryOtp, setDeliveryOtp] = useState(null);

  useEffect(() => {
    fetchOrderDetails();
  }, [id]);
  
  useEffect(() => {
    // Fetch tracking info when order is loaded
    if (order?.id) {
      fetchTrackingInfo();
    }
  }, [order?.id]);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      const data = await apiCall(`/api/orders/${id}`);
      setOrder(data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load order details',
        variant: 'destructive'
      });
      navigate('/orders');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchTrackingInfo = async () => {
    try {
      const data = await apiCall(`/api/shipping/order/${id}/tracking`);
      if (data.has_shipment) {
        setTrackingInfo(data);
        // iter-229d — if the shipment is self-managed, fetch the
        // customer-visible OTP so it can be displayed prominently on
        // this page (the customer reads it out at the doorstep when
        // the delivery person hands over the parcel).
        const sh = data.shipment || {};
        const shipmentId = sh.id || sh.shipment_id;
        if (shipmentId && (sh.shipping_method === 'self_managed' || sh.provider === 'self_managed')) {
          try {
            const otpRes = await apiCall(`/api/shipments/${shipmentId}/customer-otp`);
            if (otpRes?.otp) {
              setDeliveryOtp(otpRes);
            }
          } catch (_e) {
            // 404 (no OTP yet) / 403 (auth mismatch) — silently skip.
          }
        }
      }
    } catch (error) {
      // Silently ignore - tracking may not exist
    }
  };

  const handleDownloadInvoicePDF = async () => {
    try {
      // iter-252-AG: for parent orders (multi-vendor), use the new Consolidated
      // Purchase Receipt format. For single/child orders, standard GST invoice.
      const isParent = !!order?.is_parent_order;
      const url = isParent
        ? `${process.env.REACT_APP_BACKEND_URL}/api/invoices/consolidated/${id}?format=receipt`
        : `${process.env.REACT_APP_BACKEND_URL}/api/invoices/${id}/pdf`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${sessionStorage.getItem('token')}` }
      });
      if (!response.ok) throw new Error('Failed to generate PDF');
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = isParent
        ? `order-${order?.order_number || id}-receipt.pdf`
        : `invoice-${order?.order_number || id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(objectUrl);
      toast({ title: 'Success', description: isParent ? 'Consolidated receipt downloaded' : 'Invoice PDF downloaded' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to download invoice PDF', variant: 'destructive' });
    }
  };

  // iter-205: on parent orders, also expose the multi-page (per-seller
  // tax invoices + Aniyor logistics page) version so sellers & tax
  // compliance can be handled side-by-side.
  const handleDownloadMultiInvoices = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/invoices/consolidated/${id}?format=multi`, {
        headers: { 'Authorization': `Bearer ${sessionStorage.getItem('token')}` }
      });
      if (!response.ok) throw new Error('Failed to generate PDF');
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `order-${order?.order_number || id}-invoices.pdf`;
      a.click();
      window.URL.revokeObjectURL(objectUrl);
      toast({ title: 'Success', description: 'All seller invoices downloaded' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to download seller invoices', variant: 'destructive' });
    }
  };


  // Hyperlocal: accept order with prep time
  const handleHyperlocalAccept = async (prepMins) => {
    setHyperlocalAccepting(true);
    try {
      const data = await apiCall(`/api/shipping/hyperlocal/orders/${id}/accept`, {
        method: 'POST',
        body: JSON.stringify({ prep_minutes: prepMins }),
      });
      toast({ title: 'Order Accepted', description: data.message || `Rider dispatch in ~${prepMins} min.` });
      fetchOrderDetails();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setHyperlocalAccepting(false);
    }
  };

  // Hyperlocal: vendor ready early — dispatch immediately
  const handleHyperlocalReady = async () => {
    if (!window.confirm('Mark order as Ready for Pickup? This will dispatch a rider immediately.')) return;
    setHyperlocalReadying(true);
    try {
      const data = await apiCall(`/api/shipping/hyperlocal/orders/${id}/ready-for-pickup`, { method: 'POST' });
      toast({ title: 'Rider Dispatched!', description: 'A rider is being assigned to your order now.' });
      fetchOrderDetails();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setHyperlocalReadying(false);
    }
  };

  // Cancel an active AWB (Delhivery). Works for both admin and vendor
  // (backend is now permissive for admin/super_admin).
  const handleCancelAwb = async () => {
    if (!trackingInfo?.has_shipment || !trackingInfo?.shipment?.awb) {
      toast({
        title: 'No shipment to cancel',
        description: 'No Aniyor-tracked shipment was found for this order. If a Shopify-side AWB is set, cancel it from the Shopify admin instead.',
        variant: 'destructive',
      });
      return;
    }
    const awb = trackingInfo.shipment.awb;
    const ok = window.confirm(
      `Cancel AWB ${awb} with Delhivery?\n\nThis will mark the shipment as cancelled at Delhivery and in Aniyor. The order itself will NOT be cancelled — use "Cancel Order" for that.`
    );
    if (!ok) return;
    try {
      setAwbCancelling(true);
      const res = await apiCall(`/api/delhivery/shipments/${id}/cancel`, { method: 'POST' });
      toast({
        title: 'AWB cancelled',
        description: `AWB ${res?.awb || awb} cancelled at Delhivery.`,
      });
      fetchTrackingInfo();
      fetchOrderDetails();
    } catch (error) {
      toast({
        title: 'Cancel AWB failed',
        description: error?.message || 'Could not cancel AWB. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setAwbCancelling(false);
    }
  };

  const handleCancelOrder = async () => {
    try {
      setCancelling(true);
      // Use the full-stack admin-cancel endpoint (not /api/orders/{id}/cancel
      // which only touches Mongo). This one ALSO:
      //   • calls Shopify Admin /orders/{id}/cancel with `refund: true`
      //   • issues a Razorpay refund when the order was prepaid
      //   • writes an `order_cancellations` audit row
      //   • stamps cancellation_shopify_ok / cancellation_refund_status on the order
      const res = await apiCall(`/api/customer-orders/${id}/admin-cancel`, {
        method: 'POST',
        body: JSON.stringify({ reason: cancelReason || 'Cancelled by admin' }),
      });

      const shopifyOk = res?.shopify?.ok;
      const refundStatus = res?.refund_status;
      let description = 'Order cancelled successfully';
      if (shopifyOk === false) {
        description += ' — ⚠ Shopify cancel failed (see logs)';
      }
      if (refundStatus && refundStatus !== 'na' && refundStatus !== 'not_prepaid') {
        description += ` · refund: ${refundStatus}`;
      }

      toast({ title: 'Success', description });

      setCancelDialog(false);
      fetchOrderDetails();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setCancelling(false);
    }
  };

  const handleRectifyOrder = async () => {
    try {
      setRectifying(true);
      const res = await apiCall(`/api/orders/${id}/un-cancel`, {
        method: 'POST',
        body: JSON.stringify({
          target_status: 'delivered',
          note: rectifyNote || 'Order was fulfilled on Shopify — rectified by admin',
        }),
      });
      setRectifyResult(res);
      toast({
        title: 'Order Rectified',
        description: `Status → delivered · Net payout ₹${res.net_payout?.toLocaleString('en-IN')} · ${res.ledger_entries_created} ledger entries created`,
      });
      fetchOrderDetails();
    } catch (error) {
      toast({
        title: 'Rectification Failed',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setRectifying(false);
    }
  };

  const handleFetchPaymentId = useCallback(async () => {
    setFetchingPaymentId(true);
    try {
      const res = await apiCall(`/api/orders/${id}/fetch-payment-id`, { method: 'POST' });
      if (res?.ok && res.razorpay_payment_id) {
        toast({ title: 'Payment ID captured', description: `${res.razorpay_payment_id} (via ${res.source})` });
        fetchOrderDetails();
      } else {
        toast({ title: 'Not found', description: res?.detail || 'No pay_xxx found in Shopify transactions or Razorpay API.', variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setFetchingPaymentId(false);
    }
  }, [id]);

  const handleSaveManualPaymentId = useCallback(async () => {
    const trimmed = manualPaymentId.trim();
    if (!trimmed.startsWith('pay_') || trimmed.length < 14) {
      toast({ title: 'Invalid format', description: 'Must start with pay_ and be at least 14 characters.', variant: 'destructive' });
      return;
    }
    setSavingManualPaymentId(true);
    try {
      const res = await apiCall(`/api/orders/${id}/payment-id`, {
        method: 'PUT',
        body: JSON.stringify({ payment_id: trimmed }),
      });
      if (res?.ok) {
        const verifyNote = res.rzp_verify_status === 'captured'
          ? ' (Razorpay: captured ✓)'
          : res.rzp_verify_status === 'api_unavailable' || res.rzp_verify_status === 'skipped'
          ? ' (Razorpay verify skipped)'
          : ` (Razorpay status: ${res.rzp_verify_status})`;
        toast({ title: 'Payment ID saved', description: `${trimmed}${verifyNote}` });
        setShowManualPaymentInput(false);
        setManualPaymentId('');
        fetchOrderDetails();
      } else {
        toast({ title: 'Save failed', description: res?.detail || 'Unknown error', variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setSavingManualPaymentId(false);
    }
  }, [id, manualPaymentId]);

  const handleTriggerTransfer = async () => {
    try {
      setTriggering(true);
      const res = await apiCall(`/api/orders/${id}/trigger-transfer`, {
        method: 'POST',
        body: JSON.stringify({
          force_immediate: forceImmediate,
          note: transferNote || 'Admin-triggered payout transfer',
        }),
      });
      setTransferResult(res);
      const msg = res.already_transferred
        ? 'Transfer already existed — no duplicate created'
        : `Transfer created: ${res.transfer_id}`;
      toast({ title: 'Payout Transfer', description: msg });
      fetchOrderDetails();
    } catch (error) {
      toast({ title: 'Transfer Failed', description: error.message, variant: 'destructive' });
    } finally {
      setTriggering(false);
    }
  };

  const handleMarkDelivered = async () => {
    try {
      setMarkingDelivered(true);
      const res = await apiCall(`/api/orders/${id}/mark-delivered`, {
        method: 'POST',
        body: JSON.stringify({
          note: markDeliveredNote || 'Manually marked delivered by admin',
          trigger_payout: markDeliveredTriggerPayout,
        }),
      });
      setMarkDeliveredResult(res);
      toast({
        title: 'Order marked as Delivered',
        description: markDeliveredTriggerPayout
          ? `Payout triggered. Net: ₹${res.net_payout?.toFixed(2)}`
          : 'Status updated — payout not triggered.',
      });
      fetchOrderDetails();
    } catch (error) {
      toast({ title: 'Mark Delivered Failed', description: error.message, variant: 'destructive' });
    } finally {
      setMarkingDelivered(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">Loading order details...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!order) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">Order not found</p>
        </div>
      </DashboardLayout>
    );
  }

  const getStatusColor = (status) => {
    const colors = {
      completed: 'bg-green-100 text-green-800',
      processing: 'bg-blue-100 text-blue-800',
      pending: 'bg-yellow-100 text-yellow-800',
      cancelled: 'bg-red-100 text-red-800',
      shipped: 'bg-purple-100 text-purple-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-6xl">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/orders')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Orders
          </Button>
        </div>
        
        {/* Order Header */}
        <Card>
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-2xl font-bold">Order {order.order_number}</h1>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.status)}`}>
                    {order.status}
                  </span>
                </div>
                <div className="space-y-1 text-sm text-gray-600">
                  <p className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Order Date: {order.order_date}
                  </p>
                  {order.delivery_date && (
                    <p className="flex items-center gap-2">
                      <Truck className="h-4 w-4" />
                      Expected Delivery: {order.delivery_date}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex gap-2 flex-wrap w-full sm:w-auto">
                {/* Hyperlocal — Accept + prep time selector (workflow-critical, keep prominent) */}
                {order?.is_hyperlocal && !['hyperlocal_accepted','rider_dispatched','out_for_delivery','delivered'].includes(order?.fulfillment_status) && (
                  <div className="flex items-center gap-2" data-testid="hyperlocal-accept-group">
                    {[10, 20, 45].map((mins) => (
                      <Button
                        key={mins}
                        variant="default"
                        className="bg-orange-600 hover:bg-orange-700 text-white text-xs px-3"
                        disabled={hyperlocalAccepting}
                        onClick={() => handleHyperlocalAccept(mins)}
                        data-testid={`hyperlocal-accept-${mins}min`}
                      >
                        {hyperlocalAccepting ? '...' : `Accept · ${mins} min`}
                      </Button>
                    ))}
                  </div>
                )}
                {order?.is_hyperlocal && order?.fulfillment_status === 'hyperlocal_accepted' && (
                  <Button
                    variant="default"
                    className="bg-green-600 hover:bg-green-700 text-white"
                    disabled={hyperlocalReadying}
                    onClick={handleHyperlocalReady}
                    data-testid="hyperlocal-ready-btn"
                  >
                    <Package className="h-4 w-4 mr-2" />
                    {hyperlocalReadying ? 'Dispatching…' : 'Ready for Pickup'}
                  </Button>
                )}

                {/* Parent-order (multi-vendor) — per-child ship panel.
                    Parents themselves don't have a shipment (D-077 doctrine)
                    so the Ship Now / Ship with Delhivery buttons below are
                    hidden by shipState.can(). Instead, list each child with
                    its own state badge + Ship Now shortcut so the admin can
                    fulfil each vendor line separately. */}
                {order.is_parent_order && Array.isArray(order.children) && order.children.length > 0 && (
                  <div className="w-full mt-2 p-3 bg-indigo-50 border border-indigo-200 rounded-lg" data-testid="parent-order-children-panel">
                    <div className="text-sm font-semibold text-indigo-900 mb-2 flex items-center gap-1.5">
                      <Package className="h-4 w-4" />
                      Multi-vendor order — ship each vendor line separately
                    </div>
                    <div className="space-y-1.5">
                      {order.children.map((c) => {
                        const ctas = Array.isArray(c.shipment_ctas) ? c.shipment_ctas : [];
                        const canShip = ctas.includes('ship_with_delhivery');
                        const badgeColor = {
                          blue: 'bg-blue-100 text-blue-800',
                          emerald: 'bg-emerald-100 text-emerald-800',
                          amber: 'bg-amber-100 text-amber-800',
                          indigo: 'bg-indigo-100 text-indigo-800',
                          green: 'bg-green-100 text-green-800',
                          red: 'bg-red-100 text-red-800',
                          orange: 'bg-orange-100 text-orange-800',
                          gray: 'bg-gray-100 text-gray-700',
                        }[c.shipment_state_color] || 'bg-gray-100 text-gray-700';
                        return (
                          <div
                            key={c.id}
                            className="flex items-center justify-between gap-2 bg-white border border-indigo-100 rounded px-3 py-2"
                            data-testid={`parent-child-row-${c.id}`}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium truncate">
                                {c.name || c.id?.slice(0, 8)}
                                <span className="ml-2 text-xs text-gray-500">
                                  {c.vendor_name || c.vendor_id?.slice(0, 8) || '—'}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${badgeColor}`}>
                                  {c.shipment_state_label || c.shipment_state}
                                </span>
                                {c.awb_number && (
                                  <span className="text-[11px] text-gray-500 font-mono">AWB {c.awb_number}</span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0">
                              {canShip && (
                                <Button
                                  size="sm"
                                  className="bg-gray-900 hover:bg-gray-800 h-7 px-2.5 text-xs"
                                  onClick={() => navigate(`/orders/${c.id}`)}
                                  data-testid={`child-ship-now-${c.id}`}
                                  title="Open child order to Ship Now"
                                >
                                  <Truck className="h-3 w-3 mr-1" />
                                  Ship Now
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 text-xs"
                                onClick={() => navigate(`/orders/${c.id}`)}
                                data-testid={`child-open-${c.id}`}
                                title="Open child order"
                              >
                                Open
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* iter-298f — Delivery Location editor (SSOT).
                    Rendered above Ship Now so vendors can rescue a
                    wrong customer coord (pincode centroid / Shopify
                    rough geocode) BEFORE the Shiprocket Quick rate
                    call — otherwise the customer sees ₹0 or the
                    order gets routed via a 5-hop courier for what
                    should be a 500m Same-Day. */}
                {/* Primary CTA: Ship Now */}
                {shipState.can('ship_with_delhivery') && (
                  <Button
                    variant="default"
                    className="bg-gray-900 hover:bg-gray-800"
                    onClick={() => {
                      const phone = (order.customer?.phone || '').trim();
                      const cleanPhone = phone.replace(/[^\d]/g, '');
                      if (!phone || cleanPhone.length < 10) {
                        toast({
                          title: 'Customer mobile number is required',
                          description: phone
                            ? `The mobile number "${phone}" looks invalid. Please edit the order and ensure a 10-digit phone is saved before shipping.`
                            : 'Add a valid 10-digit mobile number on the customer details section below before generating AWB.',
                          variant: 'destructive',
                          duration: 6000,
                        });
                        setTimeout(() => {
                          const el = document.querySelector('[data-testid="customer-phone-field"]') || document.getElementById('customer-details-card');
                          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }, 100);
                        return;
                      }
                      setShipNowOpen(true);
                    }}
                    data-testid="ship-now-btn"
                  >
                    <Truck className="h-4 w-4 mr-2" />
                    Ship Now
                  </Button>
                )}

                {/* Legacy: Ship with Delhivery (direct flow, no rate-fetch) */}
                {shipState.can('ship_with_delhivery') && (
                  <Button
                    variant="outline"
                    onClick={() => setDelhiveryModalOpen(true)}
                    data-testid="ship-delhivery-direct-btn"
                    title="Legacy direct Delhivery flow — no carrier selection step"
                  >
                    <Truck className="h-4 w-4 mr-2" />
                    Ship with Delhivery
                  </Button>
                )}

                {/* Actions dropdown — all secondary/admin actions */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" data-testid="order-actions-trigger">
                      Actions
                      <ChevronDown className="h-4 w-4 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    {/* Invoice actions */}
                    <DropdownMenuItem onClick={() => navigate(`/orders/${id}/invoice`)} data-testid="view-invoice-btn">
                      <FileText className="h-4 w-4 mr-2" />
                      View Invoice
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleDownloadInvoicePDF} data-testid="download-invoice-pdf-btn">
                      <Download className="h-4 w-4 mr-2" />
                      {order.is_parent_order ? 'Consolidated Summary' : 'Download PDF'}
                    </DropdownMenuItem>
                    {order.is_parent_order && (
                      <DropdownMenuItem onClick={handleDownloadMultiInvoices} data-testid="download-multi-invoices-btn">
                        <FileText className="h-4 w-4 mr-2" />
                        Seller Invoices
                      </DropdownMenuItem>
                    )}

                    {/* Shipping actions */}
                    {shipState.can('print_label') && (order.awb_number || trackingInfo?.shipment?.awb || trackingInfo?.shipment?.label_url || order.label_url) && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          data-testid="order-card-print-label-btn"
                          onClick={async () => {
                            const url = trackingInfo?.shipment?.label_url || order.label_url || `/api/delhivery/shipments/${order.id}/label`;
                            const res = await downloadAuthedFile(url, `${order.order_number || order.id}_label.pdf`);
                            if (!res.ok) {
                              toast({ title: 'Could not download label', description: res.error || 'Try again later.', variant: 'destructive' });
                            }
                          }}
                        >
                          <Printer className="h-4 w-4 mr-2" />
                          Print Shipping Label
                        </DropdownMenuItem>
                      </>
                    )}
                    {/* iter-298L — Cancel AWB. Widen guard so the button
                        surfaces whenever a live AWB exists on the order
                        even if trackingInfo.shipment hasn't been fetched
                        yet (e.g. shipments endpoint 404s but order.awb_number
                        is stamped). shipState still authorizes the CTA. */}
                    {shipState.can('cancel_awb') && (trackingInfo?.shipment?.awb || order.awb_number) && (
                      <DropdownMenuItem
                        onClick={handleCancelAwb}
                        disabled={awbCancelling}
                        data-testid="cancel-awb-btn"
                        className="text-red-600"
                      >
                        <X className="h-4 w-4 mr-2" />
                        {awbCancelling ? 'Cancelling AWB…' : 'Cancel AWB'}
                      </DropdownMenuItem>
                    )}

                    {/* Admin / financial actions */}
                    {isSuperAdmin && (
                      <>
                        <DropdownMenuSeparator />
                        {order.status !== 'cancelled' && order.status !== 'completed' && !['delivered'].includes(order.status) && (
                          <DropdownMenuItem
                            onClick={() => { setMarkDeliveredResult(null); setMarkDeliveredDialog(true); }}
                            data-testid="mark-delivered-btn"
                            className="text-green-700"
                          >
                            <CheckSquare className="h-4 w-4 mr-2" />
                            Mark as Delivered
                          </DropdownMenuItem>
                        )}
                        {!['delivered', 'completed'].includes(order.status) && order.status !== 'cancelled' && (
                          <DropdownMenuItem
                            onClick={() => { setTransferResult(null); setTransferDialog(true); }}
                            data-testid="trigger-transfer-btn"
                          >
                            <DollarSign className="h-4 w-4 mr-2" />
                            Trigger Payout Transfer
                          </DropdownMenuItem>
                        )}
                        {['delivered', 'completed'].includes(order.status) && !order.payout_transfer_id && (
                          <DropdownMenuItem
                            onClick={() => { setTransferResult(null); setTransferDialog(true); }}
                            data-testid="trigger-transfer-btn"
                          >
                            <DollarSign className="h-4 w-4 mr-2" />
                            Trigger Payout Transfer
                          </DropdownMenuItem>
                        )}
                        {order.status !== 'cancelled' && (
                          <DropdownMenuItem
                            onClick={() => setRefundModalOpen(true)}
                            disabled={['completed', 'processed', 'processing', 'refunded'].includes(order.refund_status)}
                            data-testid="open-refund-modal-btn"
                            className="text-red-600"
                          >
                            <DollarSign className="h-4 w-4 mr-2" />
                            Refund
                          </DropdownMenuItem>
                        )}
                        {order.status === 'cancelled' && !['completed', 'processed', 'processing', 'refunded', 'not_required'].includes(order.refund_status) && (
                          <DropdownMenuItem
                            onClick={() => setRefundModalOpen(true)}
                            data-testid="process-refund-cancelled-btn"
                            className="text-orange-600"
                          >
                            <DollarSign className="h-4 w-4 mr-2" />
                            Process Refund
                          </DropdownMenuItem>
                        )}
                        {order.status === 'cancelled' && (
                          <DropdownMenuItem
                            onClick={() => { setRectifyResult(null); setRectifyDialog(true); }}
                            data-testid="rectify-order-btn"
                            className="text-amber-700"
                          >
                            <WrenchIcon className="h-4 w-4 mr-2" />
                            Rectify Order
                          </DropdownMenuItem>
                        )}
                      </>
                    )}

                    {/* Cancel order */}
                    {order.status !== 'cancelled' && order.status !== 'completed' && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => setCancelDialog(true)}
                          className="text-red-600"
                        >
                          <X className="h-4 w-4 mr-2" />
                          Cancel Order
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* iter-229d — Self-managed delivery OTP panel. Renders ONLY
                when the seller has chosen a non-Delhivery courier and
                the customer is the authenticated viewer. Customers read
                this code aloud to the delivery person at the doorstep
                so the seller can mark the parcel delivered. */}
            {deliveryOtp?.otp && (
              <div
                className="mt-4 rounded-lg border-2 border-dashed border-purple-400 bg-purple-50 p-4 md:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                data-testid="self-managed-delivery-otp-panel"
              >
                <div className="text-sm">
                  <div className="font-semibold text-purple-900 mb-0.5">
                    Delivery OTP
                  </div>
                  <div className="text-gray-700">
                    Show this 4-digit code to the {deliveryOtp.courier_name || 'delivery'} person
                    at the doorstep — they'll confirm it with the seller.
                  </div>
                  {deliveryOtp.awb && (
                    <div className="text-xs text-gray-500 mt-1">
                      AWB: <span className="font-mono">{deliveryOtp.awb}</span>
                    </div>
                  )}
                </div>
                <div
                  className="font-mono text-3xl md:text-4xl font-bold tracking-[0.5em] text-purple-800 select-all bg-white px-4 py-2 rounded border border-purple-200"
                  data-testid="delivery-otp-value"
                >
                  {deliveryOtp.otp}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Order Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Package className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Items</p>
                  <p className="text-2xl font-bold">{order.items.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                  <DollarSign className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total</p>
                  <p className="text-2xl font-bold">₹{order.pricing.total.toFixed(2)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                  <CreditCard className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Payment</p>
                  <p className="text-lg font-bold">{order.payment.method}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-yellow-100 flex items-center justify-center">
                  <Truck className="h-5 w-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Tracking</p>
                  <p className="text-sm font-bold">{order.tracking?.current_status || 'N/A'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Tabs */}
        <Tabs defaultValue="items" className="w-full">
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: 'none' }}>
            <TabsList className="w-max flex-nowrap">
              <TabsTrigger value="items" className="whitespace-nowrap">Order Items</TabsTrigger>
              <TabsTrigger value="customer" className="whitespace-nowrap">Customer Info</TabsTrigger>
              <TabsTrigger value="shipping" className="whitespace-nowrap">Shipping and Delivery</TabsTrigger>
              <TabsTrigger value="payment" className="whitespace-nowrap">Payment Details</TabsTrigger>
              <TabsTrigger value="timeline" className="whitespace-nowrap">Order Timeline</TabsTrigger>
            </TabsList>
          </div>
          
          <TabsContent value="items" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Order Items</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {order.items.map((item) => (
                    <div key={item.id} className="flex items-start gap-4 p-4 border rounded-lg" data-testid={`order-item-${item.id}`}>
                      <button
                        type="button"
                        className="h-20 w-20 bg-gray-100 rounded-lg flex items-center justify-center shrink-0 overflow-hidden focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:cursor-default"
                        disabled={!item.image}
                        onClick={() => item.image && setLightboxImage({ url: item.image, name: item.name })}
                        data-testid={`order-item-image-btn-${item.id}`}
                        aria-label={item.image ? `Open image for ${item.name}` : `No image for ${item.name}`}
                      >
                        {item.image ? (
                          <img
                            src={item.image}
                            alt={item.name}
                            className="h-20 w-20 object-cover rounded-lg hover:opacity-90 transition-opacity"
                            onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextElementSibling?.classList.remove('hidden'); }}
                          />
                        ) : null}
                        {!item.image && <Package className="h-8 w-8 text-gray-400" />}
                        <Package className="hidden h-8 w-8 text-gray-400" />
                      </button>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold">{item.name}</h4>
                        {item.variant_title && (
                          <p className="mt-1 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-blue-50 text-blue-800 text-xs font-medium border border-blue-200" data-testid={`item-variant-${item.id}`}>
                            <span className="text-blue-600">Variant:</span> {item.variant_title}
                          </p>
                        )}
                        <p className="text-sm text-gray-600 mt-1">SKU: {item.sku || '—'}</p>
                        <p className="text-sm text-gray-600">Quantity: {item.quantity}</p>
                        {item.listing_id && (
                          <a
                            href={`/listings/${LISTING_TYPE_TO_ROUTE[item.listing_type] || 'products'}/${item.listing_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-sm text-amber-700 hover:text-amber-800 hover:underline mt-1"
                            data-testid={`order-item-view-product-link-${item.id}`}
                          >
                            View Product <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                        {/* iter-230d — Inline custom options & duration so admin
                            doesn't have to click into another tab to see what
                            the customer actually configured (e.g. tiffin start
                            date, t-shirt size, rental duration multiplier). */}
                        {Array.isArray(item.product_options) && item.product_options.length > 0 && (
                          <div className="mt-2 border-l-2 border-amber-300 pl-3 space-y-0.5" data-testid={`item-options-${item.id}`}>
                            <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide">Customer-supplied options</p>
                            {item.product_options.map((opt, idx) => {
                              const label = opt.field_label || opt.field_name || opt.label || opt.name;
                              const value = opt.selected_label || opt.selected_value || opt.value;
                              if (!label && !value) return null;
                              return (
                                <p key={idx} className="text-sm text-gray-700">
                                  <span className="text-gray-500">{label}:</span> <span className="font-medium">{String(value || '—')}</span>
                                  {opt.price_impact ? (
                                    <span className="ml-1 text-xs text-gray-500">(+₹{Number(opt.price_impact).toFixed(2)})</span>
                                  ) : null}
                                </p>
                              );
                            })}
                          </div>
                        )}
                        {(item.duration_multiplier && item.duration_multiplier !== 1) || item.duration_unit ? (
                          <p className="text-xs text-gray-600 mt-1.5">
                            Duration: <span className="font-medium">{item.duration_multiplier || 1} {item.duration_unit || ''}</span>
                            {item.base_price ? <span className="ml-1 text-gray-500">(base ₹{Number(item.base_price).toFixed(2)})</span> : null}
                          </p>
                        ) : null}
                        {item.addons_total ? (
                          <p className="text-xs text-gray-600">Add-ons total: <span className="font-medium">₹{Number(item.addons_total).toFixed(2)}</span></p>
                        ) : null}
                      </div>
                      <div className="text-right shrink-0">
                        <p className="font-semibold text-lg">₹{item.price.toFixed(2)}</p>
                        <p className="text-sm text-gray-600">each</p>
                      </div>
                    </div>
                  ))}
                  
                  <div className="border-t pt-4 space-y-2">
                    {/* Items subtotal: raw pricing.subtotal includes the logistics fee — subtract it so we don't double-count */}
                    <div className="flex justify-between">
                      <span>Items Subtotal:</span>
                      <span className="font-semibold">
                        ₹{(Math.max(0, (order.pricing.subtotal || 0) - (order.aniyor_logistics_fee || 0))).toFixed(2)}
                      </span>
                    </div>
                    {/* Shipping = aniyor_logistics_fee when present, otherwise fall back to pricing.shipping */}
                    <div className="flex justify-between">
                      <span>Shipping &amp; Handling:</span>
                      <span className="font-semibold">
                        ₹{((order.aniyor_logistics_fee || order.pricing.shipping || 0)).toFixed(2)}
                      </span>
                    </div>
                    {(order.pricing.discount || 0) > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>Discount:</span>
                        <span className="font-semibold">-₹{(order.pricing.discount || 0).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-lg font-bold pt-2 border-t">
                      <span>Total:</span>
                      <span>₹{(order.pricing.total || 0).toFixed(2)}</span>
                    </div>
                    <p className="text-xs text-gray-500 italic mt-1">* All prices are inclusive of GST</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* iter-230d — Order Notes card: subscription start date,
                rental/booking schedule, Shopify customer note, tags.
                Renders only if the order actually carries any of these
                fields so regular product orders still see a clean page. */}
            <OrderNotesCard order={order} />
            {/* iter-230l — Refund history (super_admin only) */}
            {isSuperAdmin && (
              <RefundHistoryCard
                orderId={order.id}
                refreshKey={refundRefreshKey}
              />
            )}
          </TabsContent>
          
          <TabsContent value="customer" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card id="customer-details-card">
                <CardHeader>
                  <CardTitle>Customer Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-600">Name</p>
                    <p className="font-medium">{order.customer.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      Email
                    </p>
                    <p className="font-medium">{order.customer.email}</p>
                  </div>
                  <div data-testid="customer-phone-field" className={
                    !order.customer.phone || String(order.customer.phone).replace(/[^\d]/g, '').length < 10
                      ? 'p-2 -m-2 rounded border border-red-300 bg-red-50'
                      : ''
                  }>
                    <p className="text-sm text-gray-600 flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      Phone
                      {(!order.customer.phone || String(order.customer.phone).replace(/[^\d]/g, '').length < 10) && (
                        <span className="text-xs text-red-600 font-medium">⚠ Required for shipping</span>
                      )}
                    </p>
                    {(!order.customer.phone || String(order.customer.phone).replace(/[^\d]/g, '').length < 10) ? (
                      <div className="mt-2 space-y-2" data-testid="customer-phone-add-form">
                        <p className="text-xs text-red-700 italic">
                          Missing — add before shipping. Customer collected phone at checkout but Shopify did not pass it through. Enter manually below.
                        </p>
                        <div className="flex gap-2">
                          <input
                            type="tel"
                            inputMode="numeric"
                            placeholder="10-digit mobile (e.g. 9876543210)"
                            value={phoneInput}
                            onChange={(e) => setPhoneInput(e.target.value.replace(/[^\d+\s-]/g, ''))}
                            disabled={savingPhone}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            data-testid="customer-phone-input"
                            maxLength={15}
                          />
                          <Button
                            size="sm"
                            disabled={savingPhone || !phoneInput.replace(/\D/g, '').match(/^\d{10}$|^91\d{10}$/)}
                            onClick={async () => {
                              setSavingPhone(true);
                              try {
                                await apiCall(`/api/orders/${order.id}/customer-phone`, {
                                  method: 'PATCH',
                                  body: JSON.stringify({ phone: phoneInput }),
                                });
                                toast({ title: 'Phone saved', description: 'You can now generate the AWB.' });
                                setPhoneInput('');
                                await fetchOrderDetails();
                              } catch (err) {
                                toast({
                                  title: 'Could not save phone',
                                  description: err?.message || 'Please check the number and try again.',
                                  variant: 'destructive',
                                });
                              } finally {
                                setSavingPhone(false);
                              }
                            }}
                            data-testid="customer-phone-save-btn"
                          >
                            {savingPhone ? 'Saving…' : 'Save phone'}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <p className="font-medium" data-testid="customer-phone-display">{order.customer.phone}</p>
                    )}
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Vendor Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-600">Store Name</p>
                    <p className="font-medium">{order.vendor.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      Email
                    </p>
                    <p className="font-medium">{order.vendor.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      Phone
                    </p>
                    <p className="font-medium">{order.vendor.phone}</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          <TabsContent value="shipping" className="space-y-4">
            {/* iter-298f — Delivery Location SSOT editor lives inside
                Shipping & Delivery so it's contextually adjacent to
                the address + tracking blocks. Renders full-width above
                the two-column layout so the map has room to breathe
                when expanded. */}
            <DeliveryLocationCard
              order={order}
              onCoordChanged={() => fetchOrderDetails()}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle>Shipping Address</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1">
                    <p className="font-medium">{order.shipping_address.name}</p>
                    <p className="text-gray-600">{order.shipping_address.address}</p>
                    <p className="text-gray-600">
                      {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip}
                    </p>
                    <p className="text-gray-600">{order.shipping_address.country}</p>
                  </div>
                </CardContent>
              </Card>
              
              {/* Enhanced Tracking Section */}
              {trackingInfo?.has_shipment ? (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Truck className="h-5 w-5" />
                      Shipment Tracking
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-sm text-gray-600">AWB Number</p>
                        {/* iter-232.I — When AWB is cancelled, show it with
                            strikethrough + red "Cancelled" badge + the date
                            (audit trail per user spec). Otherwise plain. */}
                        {(trackingInfo.shipment?.awb_cancelled === true ||
                          String(trackingInfo.shipment?.status || '').toLowerCase() === 'cancelled') ? (
                          <div>
                            <p
                              className="font-mono font-medium line-through text-gray-500"
                              data-testid="awb-number-cancelled"
                            >
                              {trackingInfo.shipment?.awb || 'N/A'}
                            </p>
                            <span
                              className="inline-flex items-center gap-1 mt-1 rounded-full bg-red-50 border border-red-200 text-red-700 text-[10px] font-semibold px-2 py-0.5"
                              data-testid="awb-cancelled-badge"
                            >
                              <span aria-hidden="true">🔴</span> AWB Cancelled
                            </span>
                            {(trackingInfo.shipment?.awb_cancelled_at || trackingInfo.shipment?.cancelled_at) && (
                              <p
                                className="text-xs text-gray-500 mt-1"
                                data-testid="awb-cancelled-on"
                              >
                                Cancelled on {new Date(
                                  trackingInfo.shipment.awb_cancelled_at || trackingInfo.shipment.cancelled_at
                                ).toLocaleString('en-IN', {
                                  day: '2-digit', month: 'short', year: 'numeric',
                                  hour: '2-digit', minute: '2-digit',
                                })}
                              </p>
                            )}
                            {trackingInfo.shipment?.delhivery_cancel_reason && (
                              <p className="text-xs text-gray-400 mt-0.5">
                                {trackingInfo.shipment.delhivery_cancel_reason}
                              </p>
                            )}
                          </div>
                        ) : (
                          <p className="font-mono font-medium">{trackingInfo.shipment?.awb || 'N/A'}</p>
                        )}
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Provider</p>
                        <p className="font-medium">{trackingInfo.shipment?.provider || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Status</p>
                        <Badge className="bg-blue-100 text-blue-700" data-testid="shipment-status-badge">
                          {/* iter-252-AD / D-075 — Prefer the SSOT resolver
                              stamp (`shipment_state_label`) that the backend
                              attaches via `resolve_with_cta`. Falls back to
                              `canonical_state` and then raw `status` only
                              if the stamp is missing (e.g. legacy responses
                              from older endpoints). The stamp guarantees
                              consistency with the Shipping Pipeline page +
                              the orders list badge. */}
                          {(() => {
                            const ssotLabel = trackingInfo.shipment_state_label;
                            if (ssotLabel) return ssotLabel;
                            const cs = trackingInfo.shipment?.canonical_state;
                            const legacy = trackingInfo.shipment?.status;
                            const raw = cs || legacy || 'Unknown';
                            return String(raw).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                          })()}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Logistics Cost</p>
                        <p className="font-medium">₹{trackingInfo.cost_summary?.total?.toLocaleString() || '0'}</p>
                      </div>
                    </div>

                    {/* Shiprocket Quick — Live Rider Tracking Card */}
                    {trackingInfo.shipment?.live_tracking_url && (
                      <div
                        data-testid="hyperlocal-live-tracking-card"
                        className="mt-3 p-3 rounded-lg border border-orange-200 bg-orange-50"
                      >
                        <div className="flex items-start gap-2">
                          <MapPin className="h-4 w-4 text-orange-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-orange-800">Live Rider Tracking</p>
                            {trackingInfo.shipment?.quick_rider_name && (
                              <p className="text-xs text-orange-700 mt-0.5">
                                Rider: {trackingInfo.shipment.quick_rider_name}
                                {trackingInfo.shipment?.quick_rider_phone && (
                                  <span> &middot; {trackingInfo.shipment.quick_rider_phone}</span>
                                )}
                              </p>
                            )}
                            <p className="text-xs text-orange-600 mt-1 break-all">
                              {trackingInfo.shipment.live_tracking_url}
                            </p>
                          </div>
                        </div>
                        <a
                          href={trackingInfo.shipment.live_tracking_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="live-tracking-open-btn"
                          className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-md bg-orange-600 text-white text-xs font-medium px-3 py-1.5 hover:bg-orange-700 transition-colors"
                        >
                          <ExternalLink className="h-3 w-3" />
                          Open Live Map
                        </a>
                      </div>
                    )}

                    {/* iter-233 — Pickup Scheduling only when AWB is LIVE.
                        Cancelled AWBs cannot have a pickup scheduled — suppress
                        the scheduler entirely. Vendor must generate a new AWB
                        first (Ship-with-Delhivery is restored above). */}
                    {awbIsActive && (
                      <div className="mt-4 pt-4 border-t bg-amber-50 -mx-6 -mb-6 px-6 py-4 rounded-b-lg">
                        <PickupScheduler
                          order={order}
                          shipmentId={trackingInfo.shipment?.id}
                          existingPickup={trackingInfo.shipment?.pickup_date ? {
                            pickup_date: trackingInfo.shipment.pickup_date,
                            pickup_time: trackingInfo.shipment.pickup_time,
                            rider_phone: trackingInfo.shipment.rider_phone,
                          } : null}
                          onScheduled={() => {
                            fetchTrackingInfo();
                            fetchOrderDetails();
                          }}
                        />
                      </div>
                    )}
                    
                    {/* Tracking Events Timeline */}
                    {trackingInfo.tracking_events && trackingInfo.tracking_events.length > 0 && (
                      <div className="mt-4 pt-4 border-t">
                        <h4 className="text-sm font-medium mb-3">Tracking History</h4>
                        <div className="space-y-2">
                          {trackingInfo.tracking_events.map((event, idx) => (
                            <div key={`${event.canonical || event.status || 'ev'}-${idx}`} className="flex items-start gap-2 text-sm">
                              <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${idx === 0 ? 'bg-green-500' : 'bg-gray-300'}`} />
                              <div className="flex-1 min-w-0">
                                <p className="font-medium">{event.status || event.canonical}</p>
                                {event.description && event.description !== event.status && (
                                  <p className="text-gray-500 text-xs">{event.description}</p>
                                )}
                                {event.location && (
                                  <p className="text-gray-400 text-xs">{event.location}</p>
                                )}
                                <p className="text-gray-400 text-xs">
                                  {new Date(event.timestamp).toLocaleString('en-IN', {
                                    day: '2-digit', month: 'short', year: 'numeric',
                                    hour: '2-digit', minute: '2-digit',
                                  })}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {trackingInfo.shipment?.label_url && (
                      <div className="pt-3 border-t">
                        <Button 
                          variant="outline" 
                          size="sm"
                          data-testid="order-detail-download-label-btn"
                          onClick={async () => {
                            const res = await downloadAuthedFile(
                              trackingInfo.shipment.label_url,
                              `${order.order_number || order.id}_label.pdf`,
                            );
                            if (!res.ok) {
                              toast({
                                title: 'Could not open label',
                                description: res.status === 401 ? 'Session expired — please refresh.' : (res.error || 'Try again from Pack & Ship.'),
                                variant: 'destructive',
                              });
                            }
                          }}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Download Label PDF
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ) : order.tracking ? (
                <Card>
                  <CardHeader>
                    <CardTitle>Tracking Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {order.tracking.carrier && (
                      <div>
                        <p className="text-sm text-gray-600">Carrier</p>
                        <p className="font-medium">{order.tracking.carrier}</p>
                      </div>
                    )}
                    {order.tracking.tracking_number && (
                      <div>
                        <p className="text-sm text-gray-600">Tracking Number</p>
                        <p className="font-medium">{order.tracking.tracking_number}</p>
                      </div>
                    )}
                    {order.tracking.current_status && (
                      <div>
                        <p className="text-sm text-gray-600">Current Status</p>
                        <Badge>{order.tracking.current_status}</Badge>
                      </div>
                    )}
                    {order.tracking.estimated_delivery && (
                      <div>
                        <p className="text-sm text-gray-600">Estimated Delivery</p>
                        <p className="font-medium">{order.tracking.estimated_delivery}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardHeader>
                    <CardTitle>Tracking Information</CardTitle>
                  </CardHeader>
                  <CardContent className="text-center py-6">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Truck className="h-8 w-8 text-gray-400" />
                    </div>
                    <p className="text-gray-500 mb-4">No shipping label generated yet</p>
                    {order.status !== 'cancelled' && order.status !== 'completed' && (
                      <Button
                        onClick={() => setDelhiveryModalOpen(true)}
                        className="bg-blue-600 hover:bg-blue-700"
                        data-testid="ship-delhivery-shipping-tab"
                      >
                        <Truck className="h-4 w-4 mr-2" />
                        Create Shipment with Delhivery
                      </Button>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
          
          <TabsContent value="payment" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle>Payment Method</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-600">Gateway</p>
                    <p className="font-medium capitalize">{order.payment_gateway || order.payment?.payment_gateway || order.payment?.method || 'N/A'}</p>
                  </div>
                  {order.payment?.card_type && (
                    <div>
                      <p className="text-sm text-gray-600">Card Type</p>
                      <p className="font-medium">
                        {order.payment.card_type} ending in {order.payment.last_four_digits}
                      </p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-gray-600">Shopify Transaction ID</p>
                    <p className="font-mono text-xs text-gray-700 break-all">
                      {order.shopify_transaction_id || order.payment?.shopify_transaction_id || order.payment?.transaction_id || '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Razorpay Payment ID</p>
                    {(order.razorpay_payment_id || order.payment?.razorpay_payment_id) ? (
                      <div className="flex items-center gap-2">
                        <p className="font-mono text-xs text-green-700 break-all" data-testid="razorpay-payment-id">
                          {order.razorpay_payment_id || order.payment?.razorpay_payment_id}
                        </p>
                        <button
                          className="text-xs text-gray-400 hover:text-gray-600"
                          onClick={() => navigator.clipboard.writeText(order.razorpay_payment_id || order.payment?.razorpay_payment_id)}
                          title="Copy"
                        >
                          <Copy className="h-3 w-3" />
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-2" data-testid="razorpay-payment-id-missing">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-xs text-amber-600 italic">Not captured</p>
                          <button
                            onClick={handleFetchPaymentId}
                            disabled={fetchingPaymentId}
                            className="flex items-center gap-1 text-xs px-2 py-0.5 rounded border border-blue-200 text-blue-600 bg-blue-50 hover:bg-blue-100 disabled:opacity-50 transition-colors"
                            data-testid="fetch-payment-id-btn"
                            title="Fetch from Shopify transactions / Razorpay API"
                          >
                            <RefreshCw className={`h-3 w-3 ${fetchingPaymentId ? 'animate-spin' : ''}`} />
                            {fetchingPaymentId ? 'Fetching…' : 'Fetch from Razorpay'}
                          </button>
                          <button
                            onClick={() => setShowManualPaymentInput(v => !v)}
                            className="flex items-center gap-1 text-xs px-2 py-0.5 rounded border border-gray-200 text-gray-600 bg-gray-50 hover:bg-gray-100 transition-colors"
                            data-testid="toggle-manual-payment-id-btn"
                            title="Enter Razorpay payment ID manually"
                          >
                            Enter Manually
                          </button>
                        </div>
                        {showManualPaymentInput && (
                          <div className="flex items-center gap-2 flex-wrap" data-testid="manual-payment-id-form">
                            <input
                              type="text"
                              value={manualPaymentId}
                              onChange={e => setManualPaymentId(e.target.value)}
                              placeholder="pay_Xxxxxxxxxxxxxxxxxx"
                              className="font-mono text-xs border border-gray-300 rounded px-2 py-1 w-52 focus:outline-none focus:ring-1 focus:ring-blue-400"
                              data-testid="manual-payment-id-input"
                              onKeyDown={e => e.key === 'Enter' && handleSaveManualPaymentId()}
                            />
                            <button
                              onClick={handleSaveManualPaymentId}
                              disabled={savingManualPaymentId || !manualPaymentId.trim()}
                              className="text-xs px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
                              data-testid="save-manual-payment-id-btn"
                            >
                              {savingManualPaymentId ? 'Saving…' : 'Save'}
                            </button>
                            <button
                              onClick={() => { setShowManualPaymentInput(false); setManualPaymentId(''); }}
                              className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
                              data-testid="cancel-manual-payment-id-btn"
                            >
                              Cancel
                            </button>
                            {manualPaymentId && !manualPaymentId.trim().startsWith('pay_') && (
                              <p className="text-xs text-red-500 w-full" data-testid="manual-payment-id-error">Must start with pay_</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {(order.razorpay_order_id || order.payment?.razorpay_order_id) && (
                    <div>
                      <p className="text-sm text-gray-600">Razorpay Order ID</p>
                      <p className="font-mono text-xs text-gray-700 break-all">
                        {order.razorpay_order_id || order.payment?.razorpay_order_id}
                      </p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-gray-600">Payment Captured At</p>
                    <p className="text-sm text-gray-700">
                      {(order.payment_captured_at || order.payment?.payment_captured_at)
                        ? new Date(order.payment_captured_at || order.payment?.payment_captured_at).toLocaleString('en-IN')
                        : order.paid_at ? new Date(order.paid_at).toLocaleString('en-IN') : '—'}
                    </p>
                  </div>
                  {order.razorpay_payment_id_source && (
                    <div>
                      <p className="text-xs text-gray-400">
                        Source: {order.razorpay_payment_id_source}
                        {order.razorpay_payment_id_fetched_at && (
                          <span className="ml-1">· fetched {new Date(order.razorpay_payment_id_fetched_at).toLocaleString('en-IN')}</span>
                        )}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Billing Address</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1">
                    <p className="font-medium">{order.billing_address.name}</p>
                    <p className="text-gray-600">{order.billing_address.address}</p>
                    <p className="text-gray-600">
                      {order.billing_address.city}, {order.billing_address.state} {order.billing_address.zip}
                    </p>
                    <p className="text-gray-600">{order.billing_address.country}</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          <TabsContent value="timeline" className="space-y-4">
            {/* Sprint-D: Refund Status Card */}
            {order.refund_status && order.refund_status !== 'not_required' && (
              <RefundStatusCard
                order={order}
                onRefundClick={() => setRefundModalOpen(true)}
                onRepairClick={async () => {
                  try {
                    const res = await apiCall(`/api/customer-orders/${order.id}/repair-payment-id`, { method: 'POST' });
                    if (res?.ok) {
                      toast({ title: 'Payment ID recovered', description: res.message });
                      fetchOrderDetails();
                    } else {
                      toast({ title: 'Recovery failed', description: res?.message || 'Could not find payment ID from Shopify.', variant: 'destructive' });
                    }
                  } catch (e) {
                    toast({ title: 'Error', description: e?.message, variant: 'destructive' });
                  }
                }}
              />
            )}

            {/* Sprint-D: Dynamic Event Timeline */}
            <Card>
              <CardHeader>
                <CardTitle>Order Timeline</CardTitle>
              </CardHeader>
              <CardContent>
                <EventTimeline events={order.event_timeline || order.timeline || []} />

                {/* P2: Add comment */}
                {isSuperAdmin && (
                  <TimelineCommentInput
                    orderId={order.id}
                    onAdded={fetchOrderDetails}
                    apiCall={apiCall}
                  />
                )}
              </CardContent>
            </Card>

            {/* Admin override actions in timeline */}
            {isSuperAdmin && (
              <Card className="border-amber-200 bg-amber-50/40">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-amber-800 flex items-center gap-2">
                    <WrenchIcon className="h-4 w-4" />
                    Admin Override Actions
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex flex-wrap gap-3">
                  {!['delivered', 'completed', 'cancelled'].includes(order.status) && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-green-400 text-green-700 hover:bg-green-50"
                      onClick={() => { setMarkDeliveredResult(null); setMarkDeliveredDialog(true); }}
                      data-testid="timeline-mark-delivered-btn"
                    >
                      <CheckSquare className="h-4 w-4 mr-2" />
                      Mark as Delivered
                    </Button>
                  )}
                  {((['delivered', 'completed'].includes(order.status) && !order.payout_transfer_id) ||
                    !['delivered', 'completed', 'cancelled'].includes(order.status)) && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-blue-400 text-blue-700 hover:bg-blue-50"
                      onClick={() => { setTransferResult(null); setTransferDialog(true); }}
                      data-testid="timeline-trigger-payout-btn"
                    >
                      <DollarSign className="h-4 w-4 mr-2" />
                      Trigger Payout Transfer
                    </Button>
                  )}
                  {order.status === 'cancelled' && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-amber-400 text-amber-700 hover:bg-amber-50"
                      onClick={() => { setRectifyResult(null); setRectifyDialog(true); }}
                      data-testid="timeline-rectify-btn"
                    >
                      <WrenchIcon className="h-4 w-4 mr-2" />
                      Rectify Order
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Cancel Order Dialog */}
      <Dialog open={cancelDialog} onOpenChange={setCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Order</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel this order? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Cancellation Reason</label>
              <Textarea
                placeholder="Enter reason for cancellation..."
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialog(false)}>
              Keep Order
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancelOrder}
              disabled={cancelling}
            >
              {cancelling ? 'Cancelling...' : 'Cancel Order'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rectify Order Dialog — visible only on cancelled orders for super admins */}
      <Dialog open={rectifyDialog} onOpenChange={(v) => { setRectifyDialog(v); if (!v) setRectifyResult(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rectify Cancelled Order</DialogTitle>
            <DialogDescription>
              This will reverse the cancellation, set the order status to <strong>delivered</strong>,
              and create the full vendor payout + ledger entries. Use only when the order was
              actually fulfilled on Shopify.
            </DialogDescription>
          </DialogHeader>

          {!rectifyResult ? (
            <div className="space-y-4 py-2">
              <div className="rounded-md bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
                <p className="font-semibold mb-1">Order {order?.shopify_order_number || order?.order_number}</p>
                <p>Current status: <span className="font-mono font-semibold">cancelled</span> → will become <span className="font-mono font-semibold">delivered</span></p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Admin Note (optional)</label>
                <Textarea
                  placeholder="e.g. Order #1034 was fulfilled on Shopify but cancelled in seller portal by mistake"
                  value={rectifyNote}
                  onChange={(e) => setRectifyNote(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          ) : (
            <div className="space-y-3 py-2 text-sm">
              <div className="rounded-md bg-green-50 border border-green-200 px-4 py-3">
                <p className="font-semibold text-green-800 mb-2">Rectification Complete</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-gray-700">
                  <span className="text-gray-500">Status</span><span className="font-mono">{rectifyResult.new_status}</span>
                  <span className="text-gray-500">Gross collection</span><span>₹{rectifyResult.gross_collection?.toLocaleString('en-IN')}</span>
                  <span className="text-gray-500">Commission</span><span>₹{rectifyResult.commission_amount?.toLocaleString('en-IN')}</span>
                  <span className="text-gray-500">Net payout</span><span className="font-semibold">₹{rectifyResult.net_payout?.toLocaleString('en-IN')}</span>
                  <span className="text-gray-500">Ledger entries</span><span>{rectifyResult.ledger_entries_created}</span>
                  <span className="text-gray-500">Breakdown engine</span><span className="font-mono text-xs">{rectifyResult.breakdown_source}</span>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setRectifyDialog(false); setRectifyResult(null); }}>
              {rectifyResult ? 'Close' : 'Cancel'}
            </Button>
            {!rectifyResult && (
              <Button
                className="bg-amber-600 hover:bg-amber-700 text-white"
                onClick={handleRectifyOrder}
                disabled={rectifying}
                data-testid="confirm-rectify-order-btn"
              >
                {rectifying ? 'Rectifying...' : 'Confirm Rectify'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Trigger Payout Transfer Dialog — admin only, delivered orders missing transfer */}
      <Dialog open={transferDialog} onOpenChange={(v) => { setTransferDialog(v); if (!v) setTransferResult(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Trigger Payout Transfer</DialogTitle>
            <DialogDescription>
              Creates the Razorpay Route held transfer for this order's vendor payout.
              Normal hold periods apply unless you override below.
            </DialogDescription>
          </DialogHeader>

          {!transferResult ? (
            <div className="space-y-4 py-2">
              <div className="rounded-md bg-blue-50 border border-blue-200 px-4 py-3 text-sm text-blue-800">
                <p className="font-semibold mb-1">Order {order?.shopify_order_number || order?.order_number}</p>
                <p>Status: <span className="font-mono font-semibold">{order?.status}</span></p>
              </div>

              <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={forceImmediate}
                    onChange={(e) => setForceImmediate(e.target.checked)}
                    className="h-4 w-4 accent-amber-600"
                    data-testid="force-immediate-checkbox"
                  />
                  <span className="text-amber-800 font-medium">
                    Override hold period — release to vendor within 60 seconds
                  </span>
                </label>
                <p className="mt-1 text-amber-700 text-xs ml-7">
                  Use only for long-overdue orders where the 7-day (or 4-day) window has clearly elapsed.
                  Normal behaviour: Razorpay holds the transfer and auto-releases at the computed date.
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Admin Note (optional)</label>
                <Textarea
                  placeholder="e.g. Poller was disabled — triggering manually for order #1032"
                  value={transferNote}
                  onChange={(e) => setTransferNote(e.target.value)}
                  rows={2}
                />
              </div>
            </div>
          ) : (
            <div className="space-y-3 py-2 text-sm">
              <div className={`rounded-md border px-4 py-3 ${transferResult.already_transferred ? 'bg-yellow-50 border-yellow-200' : 'bg-green-50 border-green-200'}`}>
                <p className="font-semibold mb-2">{transferResult.already_transferred ? 'Already Transferred' : 'Transfer Created'}</p>
                {transferResult.transfer_id && (
                  <p className="font-mono text-xs break-all">{transferResult.transfer_id}</p>
                )}
                {transferResult.force_immediate && (
                  <p className="text-amber-700 text-xs mt-1">Hold override applied — releases within 60 seconds</p>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setTransferDialog(false); setTransferResult(null); }}>
              {transferResult ? 'Close' : 'Cancel'}
            </Button>
            {!transferResult && (
              <Button
                className="bg-green-600 hover:bg-green-700 text-white"
                onClick={handleTriggerTransfer}
                disabled={triggering}
                data-testid="confirm-trigger-transfer-btn"
              >
                {triggering ? 'Triggering...' : 'Trigger Transfer'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>



      {/* Mark as Delivered dialog */}
      <Dialog open={markDeliveredDialog} onOpenChange={(v) => { setMarkDeliveredDialog(v); if (!v) setMarkDeliveredResult(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckSquare className="h-5 w-5 text-green-600" />
              Mark as Delivered
            </DialogTitle>
            <DialogDescription>
              This will update the order status to <strong>delivered</strong>, create ledger entries, and optionally trigger the Razorpay payout transfer.
            </DialogDescription>
          </DialogHeader>
          {!markDeliveredResult ? (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Note (optional)</label>
                <Textarea
                  value={markDeliveredNote}
                  onChange={e => setMarkDeliveredNote(e.target.value)}
                  placeholder="e.g. Customer confirmed receipt via WhatsApp"
                  rows={2}
                />
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={markDeliveredTriggerPayout}
                  onChange={e => setMarkDeliveredTriggerPayout(e.target.checked)}
                  className="rounded"
                />
                Also trigger Razorpay payout transfer
              </label>
            </div>
          ) : (
            <div className="space-y-2 text-sm">
              <p className="text-green-700 font-semibold">Order marked as delivered successfully.</p>
              <p>Net payout: <strong>₹{markDeliveredResult.net_payout?.toFixed(2)}</strong></p>
              {markDeliveredResult.transfer_result?.transfer_id && (
                <p>Transfer ID: <code>{markDeliveredResult.transfer_result.transfer_id}</code></p>
              )}
              {markDeliveredResult.transfer_result?.skipped && (
                <p className="text-gray-500">Payout not triggered ({markDeliveredResult.transfer_result.skipped}).</p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setMarkDeliveredDialog(false); setMarkDeliveredResult(null); }}>
              {markDeliveredResult ? 'Close' : 'Cancel'}
            </Button>
            {!markDeliveredResult && (
              <Button
                className="bg-green-600 hover:bg-green-700 text-white"
                onClick={handleMarkDelivered}
                disabled={markingDelivered}
                data-testid="confirm-mark-delivered-btn"
              >
                {markingDelivered ? 'Processing…' : 'Mark as Delivered'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delhivery Shipping Modal — kept as fallback */}
      <Dialog open={delhiveryModalOpen} onOpenChange={setDelhiveryModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-blue-600" />
              Ship with Delhivery
            </DialogTitle>
            <DialogDescription>
              Create a shipment for order #{order?.order_number}
            </DialogDescription>
          </DialogHeader>
          {order && (
            <DelhiveryShipping
              orderId={order.id}
              orderData={order}
              onShipmentCreated={(result) => {
                fetchOrderDetails();
                setTimeout(() => { setDelhiveryModalOpen(false); }, 3000);
              }}
              onClose={() => setDelhiveryModalOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Multi-carrier Ship Now modal */}
      {order && (
        <ShipNowModal
          order={order}
          open={shipNowOpen}
          onClose={() => setShipNowOpen(false)}
          onShipped={() => { setShipNowOpen(false); fetchOrderDetails(); }}
        />
      )}

      {/* iter-230l — Admin refund modal (super_admin only) */}
      {isSuperAdmin && order && (
        <RefundModal
          order={order}
          open={refundModalOpen}
          onOpenChange={setRefundModalOpen}
          onRefunded={() => {
            // Refresh both the order and the refund history once the
            // pipeline returns. The auto-cancel path may have flipped
            // order.status — re-fetching keeps the UI honest.
            fetchOrderDetails();
            setRefundRefreshKey((k) => k + 1);
          }}
        />
      )}

      {/* iter-298L — Item-image lightbox. Opens on click of any thumbnail. */}
      <Dialog open={!!lightboxImage} onOpenChange={(open) => !open && setLightboxImage(null)}>
        <DialogContent className="max-w-3xl p-0 bg-black/95 border-0" data-testid="item-image-lightbox">
          <DialogHeader className="sr-only">
            <DialogTitle>{lightboxImage?.name || 'Item image'}</DialogTitle>
          </DialogHeader>
          {lightboxImage && (
            <div className="flex flex-col items-center justify-center gap-3 p-4">
              <img
                src={lightboxImage.url}
                alt={lightboxImage.name || ''}
                className="max-h-[80vh] max-w-full object-contain rounded"
                data-testid="item-image-lightbox-img"
              />
              {lightboxImage.name && (
                <p className="text-white text-sm font-medium" data-testid="item-image-lightbox-name">
                  {lightboxImage.name}
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default OrderDetailsNew;
